import { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingScreen from '@/components/ui/LoadingScreen';
import MainLayout from '@/components/layouts/MainLayout.tsx';
import { NorthbeamPixel } from '@/components/analytics/NorthbeamPixel';
import { AuthRoutes } from '@/routes/funnel/AuthRoutes';
import { UserRoutes } from '@/routes/funnel/UserRoutes';
import { ProductRoutes } from '@/routes/funnel/ProductRoutes';
import { CartRoutes } from '@/routes/funnel/CartRoutes';
import { InterstitialRoutes } from '@/routes/funnel/InterstitialRoutes';
import MembersPortalLayout from '@/components/layouts/MembersPortal';
import ProtectedRoutes from '@/routes/protectedRoutes';
import { PreQuestionnaireRoutes } from '@/routes/funnel/PreQuestionnaireRoutes';
import { useFunnelStore } from '@/store/funnelStore';

const NotFound = lazy(() => import('@/pages/NotFound'));
const Checkout = lazy(() => import('@/pages/funnel/checkout/Checkout'));
const UserVerification = lazy(() => import('@/pages/funnel/verifications/UserVerification'));
const OnyxCallback = lazy(() => import('@/pages/auth/OnyxCallback'));
const InterstitialTest = lazy(() => import('@/pages/InterstitialTest.tsx'));

export function AppRoutes() {
  const location = useLocation();
  const { isGoingBack } = useFunnelStore();
  const isProduction = import.meta.env['VITE_DEPLOYMENT_ENVIRONMENT'] === 'production';

  return (
    <>
      <NorthbeamPixel />
      <Suspense fallback={<LoadingScreen />}>
        <AnimatePresence
          mode="wait"
          onExitComplete={() => {
            window.scrollTo({ top: 0, behavior: 'auto' });
          }}
        >
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, x: isGoingBack ? -50 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isGoingBack ? 50 : -50 }}
            transition={{ duration: 0.5 }}
          >
            <Routes location={location}>
              <Route element={<MainLayout />}>
                <Route element={<ProtectedRoutes type="public" />}>
                  {PreQuestionnaireRoutes()}
                  {AuthRoutes()}
                </Route>
                <Route element={<ProtectedRoutes />}>
                  {UserRoutes()}
                  {ProductRoutes()}
                  {CartRoutes()}
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/user_verifications" element={<UserVerification />} />
                </Route>
                {/* Interstitial routes - accessible without authentication */}
                {InterstitialRoutes()}
                {!isProduction && (
                  <Route path="/interstitial-test" element={<InterstitialTest />} />
                )}
                <Route path="/auth/onyx/callback" element={<OnyxCallback />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              <Route element={<ProtectedRoutes />}>
                <Route path="/members" element={<MembersPortalLayout />}>
                  <Route path="dashboard" element={<div>Dashboard</div>} />
                </Route>
              </Route>
            </Routes>
          </motion.div>
        </AnimatePresence>
      </Suspense>
    </>
  );
}
